import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Badge,
} from "@chakra-ui/react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FaCheckCircle, FaTimesCircle, FaArrowRight } from "react-icons/fa";

// ─── Brand tokens ──────────────────────────────────────────────────────────────
export const TEAL = "#007188";
export const TEAL_DARK = "#024856";
export const TEAL_LIGHT = "#5AB3C5";
export const FROST = "#E9F0F1";

// ─── Motion wrappers ──────────────────────────────────────────────────────────
export const MotionBox = motion(Box);

// ─── Fade-up variant ──────────────────────────────────────────────────────────
export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay },
  }),
};

// ─── Reveal wrapper ───────────────────────────────────────────────────────────
export const Reveal = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -80px 0px" });
  return (
    <MotionBox
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      custom={delay}
    >
      {children}
    </MotionBox>
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
export const Section = ({
  children,
  bg = "white",
  id,
}: {
  children: React.ReactNode;
  bg?: string;
  id?: string;
}) => (
  <Box
    as="section"
    id={id}
    bg={bg}
    py={{ base: 16, md: 24 }}
    px={{ base: 5, md: 8 }}
  >
    <Container maxW="6xl">{children}</Container>
  </Box>
);

// ─── Pill label ───────────────────────────────────────────────────────────────
export const Pill = ({ children }: { children: React.ReactNode }) => (
  <Badge
    display="inline-flex"
    alignItems="center"
    gap={1.5}
    px={3}
    py={1}
    borderRadius="full"
    bg={`${TEAL}18`}
    color={TEAL}
    fontWeight={700}
    fontSize="xs"
    letterSpacing="wider"
    textTransform="uppercase"
    border="1px solid"
    borderColor={`${TEAL_LIGHT}55`}
  >
    {children}
  </Badge>
);

// ─── CTA Buttons ──────────────────────────────────────────────────────────────
export const PrimaryBtn = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <Button
    as="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    size="lg"
    px={8}
    py={6}
    bg={TEAL}
    color="white"
    fontWeight={700}
    borderRadius="xl"
    textDecoration="none"
    _hover={{
      bg: TEAL_DARK,
      transform: "translateY(-2px)",
      boxShadow: `0 10px 30px ${TEAL}44`,
      textDecoration: "none",
    }}
    _active={{ transform: "translateY(0)" }}
    transition="all 0.22s ease"
    rightIcon={<FaArrowRight />}
  >
    {children}
  </Button>
);

export const GhostBtn = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <Button
    as="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    size="lg"
    px={8}
    py={6}
    bg="transparent"
    color={TEAL}
    fontWeight={700}
    borderRadius="xl"
    border="1.5px solid"
    borderColor={`${TEAL}44`}
    textDecoration="none"
    _hover={{
      bg: `${TEAL}0e`,
      borderColor: TEAL,
      transform: "translateY(-2px)",
      textDecoration: "none",
    }}
    _active={{ transform: "translateY(0)" }}
    transition="all 0.22s ease"
  >
    {children}
  </Button>
);

// ─── Feature card ─────────────────────────────────────────────────────────────
export const FeatureCard = ({
  icon,
  title,
  body,
  delay = 0,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  delay?: number;
}) => (
  <Reveal delay={delay}>
    <Box
      bg="white"
      borderRadius="2xl"
      p={7}
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 2px 12px rgba(0,113,136,0.06)"
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: `0 16px 40px ${TEAL}18`,
        borderColor: TEAL_LIGHT,
      }}
      transition="all 0.25s ease"
      h="full"
    >
      <Box
        w={12}
        h={12}
        borderRadius="xl"
        bg={`${TEAL}14`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={TEAL}
        mb={4}
      >
        <Icon as={icon} boxSize={5} />
      </Box>
      <Heading as="h3" size="sm" fontWeight={700} color={TEAL_DARK} mb={2}>
        {title}
      </Heading>
      <Text fontSize="sm" color="gray.500" lineHeight={1.7}>
        {body}
      </Text>
    </Box>
  </Reveal>
);

// ─── Check/X card ─────────────────────────────────────────────────────────────
export const CheckCard = ({
  text,
  type,
  delay,
}: {
  text: string;
  type: "check" | "x";
  delay?: number;
}) => (
  <Reveal delay={delay}>
    <HStack
      align="start"
      gap={4}
      bg="white"
      borderRadius="xl"
      p={5}
      border="1px solid"
      borderColor="gray.100"
      boxShadow="sm"
      _hover={{ boxShadow: "md", transform: "translateY(-2px)" }}
      transition="all 0.2s ease"
    >
      <Icon
        as={type === "check" ? FaCheckCircle : FaTimesCircle}
        color={type === "check" ? "green.400" : "red.400"}
        boxSize={5}
        mt={0.5}
        flexShrink={0}
      />
      <Text fontSize="sm" color="gray.700" lineHeight={1.65}>
        {text}
      </Text>
    </HStack>
  </Reveal>
);

// ─── Step ─────────────────────────────────────────────────────────────────────
export const Step = ({
  num,
  title,
  body,
  delay,
}: {
  num: string;
  title: string;
  body: string;
  delay?: number;
}) => (
  <Reveal delay={delay}>
    <VStack align="start" gap={3}>
      <Box
        w={25}
        h={25}
        borderRadius="xl"
        bgGradient={`linear(135deg, ${TEAL}, ${TEAL_LIGHT})`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text
          fontWeight={800}
          fontSize="sm"
          color="white"
          fontFamily="'Bricolage Grotesque', sans-serif"
        >
          {num}
        </Text>
      </Box>
      <Heading as="h3" size="sm" fontWeight={700} color={TEAL_DARK}>
        {title}
      </Heading>
      <Text fontSize="sm" color="gray.500" lineHeight={1.7}>
        {body}
      </Text>
    </VStack>
  </Reveal>
);
